/* eslint no-param-reassign: 0 */
import { DirectoryGroupingConsts } from 'client/util/Constants';
import { cloneDeep, omit } from 'lodash';
import CommandsApi from 'client/api/v2/CommandsApi';
import CronParser, { getDefaultCron } from 'client/Commands/CronParser';
import DateUtil from 'client/util/DateUtil';
import MspState from 'client/components/models/MspState';
import OrganizationsApi from 'client/api/v2/OrganizationsApi';
import ProvidersApi from 'client/api/v2/ProvidersApi';
import Vue from 'vue';
import async from 'async';
import commandTypes from 'client/Commands/constants/CommandTypes';
import createModel from 'client/store/modules/models/createModel';
import moment from 'moment';
import repeatTypes from 'client/Commands/constants/RepeatTypes';
import timeToLiveLimits from 'client/Commands/constants/TimeToLiveLimits';
import timeToLiveSmartOptions from 'client/Commands/constants/TimeToLiveSmartOptions';
import timeUtils from 'client/util/TimeUtils';

const { groupTypes, memberTypes } = DirectoryGroupingConsts;

let previousUserValue = null;

export const getDefaultItem = () => ({
  command: '',
  commandType: 'linux',
  // This array contains the ids of the files associated to the command
  files: [],
  launchType: 'manual',
  name: '',
  schedule: 'immediate',
  scheduleRepeatType: 'day',
  timeout: '120', // API needs this to be string, for whatever reason
  trigger: '',
  timeToLiveSeconds: 259200, // 3 days
});

const model = createModel(getDefaultItem, 'id', CommandsApi);

export default {
  namespaced: true,

  state: {
    ...model.state,
    date: null,
    // This array contains the actual file data
    previousFiles: [],
    files: [],
    time: null,
    basedOnTemplate: null,
  },

  actions: {
    ...model.actions,

    addFile(context, file) {
      context.commit('ADD_FILE', file);
    },

    bindCommandToSystems(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.command,
        targetType: memberTypes.system,
      });
    },

    bindCommandToSystemGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.command,
        targetType: groupTypes.systemGroup,
      });
    },

    deleteFile(context, index) {
      context.commit('DELETE_FILE', index);
    },

    fetchItem(context = { getters: { id: {} } }) {
      const { id } = context.getters;

      if (id) {
        return CommandsApi
          .get(id)
          .then((item) => {
            // Clone the item so that any changes to non-primitive type values,
            // such as an array of phone numbers, are _not_ accidentally made
            // to the previous item when the item is mutated.

            context.commit('SET_ITEM', cloneDeep(item));
            context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));

            if (item.files.length) {
              CommandsApi.getFiles(id).then((res) => {
                context.commit('SET_FILES', cloneDeep(res.data.results));
                context.commit('SET_PREVIOUS_FILES', cloneDeep(res.data.results));
                return item;
              });
            }

            const minLimitSeconds = timeUtils.hoursToSeconds(
              timeToLiveLimits.minTimeToLiveHours,
            );
            if (!item.timeToLiveSeconds || item.timeToLiveSeconds < minLimitSeconds) {
              context.commit('SET_TIME_TO_LIVE', minLimitSeconds);
            }
          });
      }

      return Promise.resolve();
    },

    resetItem({ commit }) {
      commit('RESET_ITEM');
    },

    async saveItem(context) {
      const { getters = {} } = context;

      const handleSaveItem = (item) => {
        context.commit('SET_ITEM', cloneDeep(item));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
        context.commit('SET_ITEM_STATE', { update: true });
        return item;
      };

      // Create or delete any files
      await context.dispatch('updateFiles');

      if (getters.isItemBeingCreated) {
        await CommandsApi.create(getters.item).then(handleSaveItem);
      } else {
        await CommandsApi.update(getters.item).then(handleSaveItem);
      }

      await context.dispatch('fetchItem');
    },
    async saveAndCreateTemplate(context) {
      const { getters = {} } = context;

      const handleSaveItem = (item) => {
        context.commit('SET_ITEM', cloneDeep(item));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
        context.commit('SET_ITEM_STATE', { update: true });
        return item;
      };

      // Create or delete any files
      await context.dispatch('updateFiles');
      const { item, commandTemplatedisplayName } = getters;
      let commandCreated;
      if (getters.isItemBeingCreated) {
        commandCreated = await CommandsApi.create(item).then(handleSaveItem);
      } else {
        commandCreated = await CommandsApi.update(item).then(handleSaveItem);
      }
      const customItem = { ...commandCreated, name: commandTemplatedisplayName };
      if (context.rootGetters['SettingsModule/isProvider']) {
        const providerId = context.rootGetters['SettingsModule/getUser'].provider;

        await ProvidersApi.createCommandTemplate(providerId, omit(customItem, ['organization']));
      } else {
        await OrganizationsApi.createCommandTemplate(MspState.orgId, customItem);
      }
      await context.dispatch('fetchItem');
    },

    setItemField(context, { key, value }) {
      // When changing the command type, certain fields should be emptied out
      if (key === 'commandType') {
        if (context.state.item.user) {
          // Store 'users' field if it had been set previously
          previousUserValue = context.state.item.user;
        }

        if (value === commandTypes.windows) {
          // Clear 'users' field
          model.actions.setItemField(context, { key: 'user', value: null });

          // Set default Windows powershell value
          model.actions.setItemField(context, { key: 'shell', value: 'cmd' });
        } else {
          // Restore 'users' field
          model.actions.setItemField(context, { key: 'user', value: previousUserValue });

          // Clear Windows powershell value
          model.actions.setItemField(context, { key: 'shell', value: '' });
        }
      }

      // When changing the launch type, set the 'schedule' field
      if (key === 'launchType') {
        if (value === 'manual' || value === 'trigger') {
          model.actions.setItemField(context, { key: 'schedule', value: 'immediate' });
        }
        if (value !== 'trigger') {
          model.actions.setItemField(context, { key: 'trigger', value: '' });
        }
      }

      model.actions.setItemField(context, { key, value });
    },

    setRepeatingSchedule({ dispatch, getters, state }, payload) {
      // Use given values in parameters, unless not present, then use computed value
      const repeatEvery = payload.repeatEvery || getters.repeatEvery;
      const repeatEveryMonthOnDay = payload.repeatEveryMonthOnDay || getters.repeatEveryMonthOnDay;
      const repeatOnDays = payload.repeatOnDays || getters.repeatOnDays;
      const runAtTime = payload.runAtTime || getters.runAtTime;

      const cron = getDefaultCron();

      const setRunAtTime = () => {
        if (runAtTime) {
          [cron.hours[0].value, cron.minutes[0].value] = runAtTime.split(':');
        }
      };

      switch (state.item.scheduleRepeatType) {
        case repeatTypes.minute:
          cron.minutes[0].repeatEvery = repeatEvery;
          break;
        case repeatTypes.hour:
          cron.minutes[0].value = 0;
          cron.hours[0].repeatEvery = repeatEvery;
          break;
        case repeatTypes.day:
          setRunAtTime();
          cron.days[0].repeatEvery = repeatEvery;
          break;
        case repeatTypes.week:
          if (repeatOnDays) {
            Object.keys(cron.weekdays).forEach((weekday) => {
              cron.weekdays[weekday] = repeatOnDays.includes(weekday);
            });
          }
          setRunAtTime();
          break;
        case repeatTypes.month:
          cron.months[0].value = repeatEvery;
          if (repeatEveryMonthOnDay) {
            cron.days[0].value = repeatEveryMonthOnDay;
          }
          setRunAtTime();
          break;
        default:
          break;
      }

      dispatch('setItemField', {
        key: 'schedule',
        value: CronParser.getCronString(cron),
      });
    },

    setScheduledRunDateTime({ commit, dispatch, getters }, payload) {
      const date = payload.date || getters.date || getters.scheduledRunDate || new Date();
      const time = payload.time || getters.time || getters.scheduledRunTime;
      const dateMoment = moment(date, 'YYYY/MM/DD');
      const timeMoment = moment(time, 'HH:mm');
      const dateAvailable = dateMoment !== null && dateMoment.isValid();
      const timeAvailable = timeMoment !== null && timeMoment.isValid();

      if (dateAvailable && !timeAvailable) {
        commit('SET_DATE', dateMoment);
      } else if (timeAvailable && !dateAvailable) {
        commit('SET_TIME', timeMoment);
      } else if (dateAvailable && timeAvailable) {
        const cron = getDefaultCron();
        cron.minutes[0].value = timeMoment.format('mm');
        cron.hours[0].value = timeMoment.format('HH');
        cron.days[0].value = dateMoment.format('D');
        cron.months[0].value = dateMoment.format('M');

        dispatch('setItemField', {
          key: 'schedule',
          value: CronParser.getCronString(cron),
        });

        dispatch('setItemField', {
          key: 'scheduleYear',
          value: dateMoment.year(),
        });
      }
    },

    updateFileDestination(context, { index, value }) {
      context.commit('UPDATE_FILE_DESTINATION', { index, value });
    },

    updateFiles(context) {
      return new Promise((resolve) => {
        const { files, item, previousFiles } = context.state;
        const subtractions = previousFiles
          .filter(previousFile => !files.some(file => previousFile.name === file.name));
        const additions = files
          .filter(file => !previousFiles.some(previousFile => file.name === previousFile.name));
        const changes = files.filter(file => previousFiles
          .some(previousFile => file.name === previousFile.name
            && file.destination !== previousFile.destination));
        const tasks = [];

        if (subtractions.length) {
          subtractions
            .forEach(removedFile => tasks.push(cb => CommandsApi.deleteFile(removedFile).then(cb)));
        }

        if (additions.length) {
          additions
            .forEach(file => tasks.push((cb) => {
              CommandsApi.addFile(file).then((res) => {
                const newFiles = [...item.files, res.data._id];
                model.actions.setItemField(context, { key: 'files', value: newFiles });
                cb();
              });
            }));
        }

        if (changes.length) {
          changes.forEach(file => tasks.push(cb => CommandsApi.modifyFile(file).then(cb)));
        }

        async.parallel(tasks, () => resolve());
      });
    },

    handleSmartTimeToLiveChange(context, { value }) {
      let timeToLiveSeconds = cloneDeep(context.state.item.timeToLiveSeconds);

      const smartOption = timeToLiveSmartOptions.find(option => option.value === value);
      if (smartOption) timeToLiveSeconds = smartOption.secondsValue;

      context.commit('SET_TIME_TO_LIVE', timeToLiveSeconds);
    },

    handleDaysTimeToLiveChange(context, { value }) {
      let timeToLiveSeconds = cloneDeep(context.state.item.timeToLiveSeconds);

      value = parseInt(value, 10);
      if ((!value && value !== 0) || value < 0) return;

      const daysInSeconds = timeUtils.daysToSeconds(value);
      let preHoursInSeconds = 0;
      if (value < timeToLiveLimits.maxTimeToLiveDays) {
        preHoursInSeconds = timeUtils.hoursToSeconds(context.getters.timeToLive.timeToLiveHours);
      }
      timeToLiveSeconds = daysInSeconds + preHoursInSeconds;

      context.commit('SET_TIME_TO_LIVE', timeToLiveSeconds);
    },

    handleHoursTimeToLiveChange(context, { value }) {
      let timeToLiveSeconds = cloneDeep(context.state.item.timeToLiveSeconds);

      value = parseInt(value, 10);
      if ((!value && value !== 0) || value < 0) return;

      if (value <= 23) {
        const hoursInSeconds = timeUtils.hoursToSeconds(value);
        const preDaysInSeconds = timeUtils.daysToSeconds(
          context.getters.timeToLive.timeToLiveDays,
        );
        timeToLiveSeconds = hoursInSeconds + preDaysInSeconds;

        context.commit('SET_TIME_TO_LIVE', timeToLiveSeconds);
      }
    },
  },

  getters: {
    ...model.getters,

    /**
     * An intermediary object that represents the `schedule` string field with an object.
     * It's used as an in-between to more easily interpret and modify the `schedule` field.
     */
    cron(state) { return CronParser.parseCronTab(state.item.schedule); },

    date(state) { return state.date; },

    commandTemplatedisplayName(state) {
      const date = DateUtil.formatDateTimestamp(new Date());
      const name = `${state.item.name} [Template-${date}]`;
      return name;
    },

    displayName(state) { return state.item.name; },

    haveAssociationsChanged(state, getters) {
      const associationTypes = [memberTypes.system, groupTypes.systemGroup];
      return model.getters.haveAssociationsChanged(state, getters)(associationTypes);
    },

    repeatEvery(state, getters) {
      const { cron } = getters;
      switch (state.item.scheduleRepeatType) {
        case repeatTypes.minute:
          return cron.minutes[0].repeatEvery;
        case repeatTypes.hour:
          return cron.hours[0].repeatEvery;
        case repeatTypes.day:
          return cron.days[0].repeatEvery;
        case repeatTypes.week:
          return '';
        case repeatTypes.month:
          return cron.months[0].value ? String(cron.months[0].value) : '';
        default:
          return '';
      }
    },

    repeatEveryMonthOnDay(state, getters) {
      return getters.cron.days[0].value || '';
    },

    repeatOnDays(state, getters) {
      const { cron } = getters;
      return Object.keys(cron.weekdays).filter(weekday => cron.weekdays[weekday]);
    },

    runAtTime(state, getters) {
      const { cron } = getters;
      if (cron.hours[0].value === null || cron.minutes[0].value === null) {
        return '';
      }
      return moment(`${cron.hours[0].value}:${cron.minutes[0].value}`, 'HH:mm').format('HH:mm');
    },

    scheduledRunDate(state, getters) {
      const { cron } = getters;
      if (cron.months[0].value === null || cron.days[0].value === null) {
        return '';
      }

      const year = moment().year();
      const month = moment(cron.months[0].value.toString(), 'M');
      const day = moment(cron.days[0].value.toString(), 'D');
      if (year === null || month === null || day === null || !month.isValid() || !day.isValid()) {
        return '';
      }

      return `${year}/${month.format('MM')}/${day.format('DD')}`;
    },

    scheduledRunTime(state, getters) {
      const { cron } = getters;
      if (cron.hours[0].value === null || cron.minutes[0].value === null) {
        return '';
      }

      const hour = moment(cron.hours[0].value.toString(), 'H');
      const minutes = moment(cron.minutes[0].value.toString(), 'm');
      if (hour === null || minutes === null || !hour.isValid() || !minutes.isValid()) {
        return '';
      }

      return `${hour.format('HH')}:${minutes.format('mm')}`;
    },

    time(state) { return state.time; },

    timeToLive(state) {
      let timeToLiveSmart = null;
      const timeToLiveSmartAux = timeToLiveSmartOptions.find(
        option => option.secondsValue === state.item.timeToLiveSeconds,
      );
      if (timeToLiveSmartAux) {
        timeToLiveSmart = timeToLiveSmartAux.value;
      }

      const timeToLiveDays = timeUtils.secondsToDays(state.item.timeToLiveSeconds);
      const timeToLiveHours = timeUtils.secondsToHours(state.item.timeToLiveSeconds);

      return {
        timeToLiveSmart,
        timeToLiveDays,
        timeToLiveHours,
      };
    },
  },

  mutations: {
    ...model.mutations,

    ADD_FILE(state = { files: [] }, file) {
      state.files.push(file);
    },

    DELETE_FILE(state = { files: [] }, index) {
      const { files } = state;
      files.splice(index, 1);
      Vue.set(state, 'files', files);
    },

    RESET_ITEM(state = { itemState: {} }) {
      state.item = getDefaultItem();
      state.itemState.create = undefined;
      state.itemState.update = undefined;
      state.previousItem = getDefaultItem();
      state.previousFiles = [];
      state.files = [];
      state.date = null;
      state.time = null;
    },

    SET_DATE(state = { date: null }, date) {
      state.date = date;
    },

    SET_FILES(state = { files: [] }, files) {
      Vue.set(state, 'files', files);
    },

    SET_PREVIOUS_FILES(state = { previousFiles: [] }, previousFiles) {
      Vue.set(state, 'previousFiles', previousFiles);
    },

    SET_TIME(state = { time: null }, time) {
      state.time = time;
    },

    UPDATE_FILE_DESTINATION(state = { files: [] }, { index, value }) {
      const updatedFile = {
        ...state.files[index],
        destination: value,
      };
      Vue.set(state.files, index, updatedFile);
    },

    SET_TIME_TO_LIVE(state = { item: {} }, timeToLive) {
      state.item.timeToLiveSeconds = timeToLive;
    },
  },
};
